import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, TextField, Box } from "@mui/material";
import ArgonButton from "components/ArgonButton";
import ArgonTypography from "components/ArgonTypography";
import ArgonSelect from "components/ArgonSelect";
import { getParentCategories } from "services/categoriesService";
import {
  addSubCategory,
  updateSubCategory,
} from "services/subCategoriesService";
import { toast } from "react-toastify";
import LanguageInput from "components/LanguageInput";
import { SUPPORTED_LANGUAGES } from "constants/languages";

const getDialogMaxHeight = (count) => {
  if (count <= 3) return "300px";
  if (count <= 6) return "500px";
  return "500px";
};

const SubCategoryModal = ({ open, onClose, category, onSuccess }) => {
  const [translations, setTranslations] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectFocused, setSelectFocused] = useState(false);

  const isCategoryFromContext = category && category.name && category.value;
  const isEditMode = !!category && !isCategoryFromContext;

  // Initialize translations for all supported languages
  const initializeTranslations = (existingTranslations = []) => {
    const translations = SUPPORTED_LANGUAGES.map((lang) => {
      const existing = existingTranslations.find(
        (t) => t.language === lang.code
      );
      return {
        language: { label: lang.name, value: lang.code },
        name: existing ? existing.name : "",
      };
    });
    return translations;
  };

  useEffect(() => {
    const fetchParentCategories = async () => {
      if (!isCategoryFromContext) {
        const response = await getParentCategories();
        if (response.success) {
          setCategories(response.data);
        }
      }
    };
    fetchParentCategories();
  }, [isCategoryFromContext]);

  useEffect(() => {
    if (category) {
      if (isCategoryFromContext) {
        setTranslations(initializeTranslations());
        setSelectedCategory({
          label: category.name,
          value: category.value,
        });
      } else {
        // If editing, populate with existing translations or create default
        const existingTranslations = category.translations || [];
        setTranslations(initializeTranslations(existingTranslations));

        const parentCategory = categories.find(
          (cat) => cat._id === category.parent_id
        );
        if (parentCategory) {
          // Use English translation if available, otherwise fall back to name
          const englishTranslation = parentCategory.translations?.find(
            (t) => t.language === "en"
          );
          const displayName = englishTranslation
            ? englishTranslation.name
            : parentCategory.name;

          setSelectedCategory({
            label: displayName,
            value: parentCategory._id,
          });
        } else {
          setSelectedCategory("");
        }
      }
    } else {
      setTranslations(initializeTranslations());
      setSelectedCategory("");
    }
  }, [category, open, isCategoryFromContext, categories]);

  const handleTranslationChange = (index, field, value) => {
    setTranslations((prev) =>
      prev.map((translation, i) =>
        i === index ? { ...translation, [field]: value } : translation
      )
    );
  };

  const handleSubmit = async () => {
    // Validate that all translations have names
    const emptyTranslations = translations.filter((t) => !t.name.trim());

    if (emptyTranslations.length > 0) {
      const missingLanguages = emptyTranslations
        .map((t) => t.language.label)
        .join(", ");
      toast.error(
        `Please fill in sub-category names for all languages: ${missingLanguages}`
      );
      return;
    }

    if (!selectedCategory) {
      toast.error("Main category is required");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        translations: translations.map((t) => ({
          language: t.language.value,
          name: t.name.trim(),
        })),
        parent_id: selectedCategory.value,
      };

      if (isEditMode) {
        await updateSubCategory(category._id, payload);
      } else {
        await addSubCategory(payload);
      }

      setLoading(false);
      onSuccess();
      onClose();
    } catch (err) {
      setLoading(false);
      toast.error(err.response?.data?.message || "An error occurred.");
    }
  };

  const handleFocus = () => {
    setSelectFocused(true);
  };

  const handleBlur = () => {
    setSelectFocused(false);
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableBackdropClick
      PaperProps={{
        style: {
          borderRadius: "16px",
          padding: "40px 32px",
          textAlign: "center",
          backgroundColor: "#ffffff",
        },
      }}
    >
      <DialogContent
        sx={{
          minHeight: selectFocused
            ? getDialogMaxHeight(categories.length)
            : "auto",
          overflowY: "auto",
          transition: "min-height 0.3s ease-in-out",
        }}
      >
        <ArgonTypography variant="h4" mb={3}>
          {isEditMode
            ? "Edit Sub Category"
            : isCategoryFromContext
            ? `Add New Sub Category to "${category.name}"`
            : "Add New Sub Category"}
        </ArgonTypography>

        <Box display="flex" flexDirection="column" gap={3}>
          <Box mb={3} textAlign="left">
            <ArgonTypography variant="h6" fontWeight="bold" mb={1}>
              Main Category
            </ArgonTypography>
            <ArgonSelect
              onBlur={handleBlur}
              onMenuOpen={handleFocus}
              options={
                isCategoryFromContext && selectedCategory
                  ? [selectedCategory]
                  : categories.map((category) => {
                      // Use English translation if available, otherwise fall back to name
                      const englishTranslation = category.translations?.find(
                        (t) => t.language === "en"
                      );
                      const displayName = englishTranslation
                        ? englishTranslation.name
                        : category.name;

                      return {
                        label: displayName,
                        value: category._id,
                      };
                    })
              }
              value={selectedCategory}
              onChange={(selectedValue) => {
                setSelectedCategory(selectedValue);
                // Trigger onBlur when an item is selected
                if (selectedValue) {
                  handleBlur();
                }
              }}
              placeholder="Select Main Category"
              disable={loading || isCategoryFromContext}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  backgroundColor: "#fff",
                },
                "& input::placeholder": {
                  color: "#94A3B8",
                  fontSize: 14,
                },
                "& .MuiInputBase-input": {
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#172a4d",
                },
              }}
            />
          </Box>

          {/* Language Translations */}
          <Box>
            <ArgonTypography
              variant="h6"
              fontWeight="bold"
              mb={2}
              textAlign="left"
            >
              Sub Category Names in Different Languages
            </ArgonTypography>
            <ArgonTypography
              variant="body2"
              color="textSecondary"
              mb={3}
              textAlign="left"
            >
              Fill in the sub-category name for each language. At least one
              language is required.
            </ArgonTypography>

            {translations.map((translation, index) => (
              <Box key={index} mb={2}>
                <LanguageInput
                  language={translation.language}
                  name={translation.name}
                  onLanguageChange={(value) =>
                    handleTranslationChange(index, "language", value)
                  }
                  onNameChange={(e) =>
                    handleTranslationChange(index, "name", e.target.value)
                  }
                  disabled={loading}
                  languageLabel="Language"
                  nameLabel="Sub Category Name"
                  languagePlaceholder="Select Language"
                  namePlaceholder="Enter sub category name in this language"
                />
              </Box>
            ))}
          </Box>

          <Box display="flex" justifyContent="center" gap={2}>
            <ArgonButton
              variant="outlined"
              color="secondary"
              onClick={onClose}
              disabled={loading}
              sx={{
                minWidth: 100,
                backgroundColor: "#ffff",
                color: "#2d3f50",
              }}
            >
              Cancel
            </ArgonButton>
            <ArgonButton
              variant="contained"
              color="#2d3f50"
              onClick={handleSubmit}
              disabled={loading || translations.some((t) => !t.name.trim())}
              sx={{
                minWidth: 100,
                backgroundColor: "#2d3f50",
                color: "#ffff",
                "&:hover": { backgroundColor: "#2d3f50" },
                "&:disabled": {
                  backgroundColor: "#2d3f50",
                  color: "#ffff",
                },
              }}
            >
              {loading ? "Saving..." : isEditMode ? "Save" : "Add"}
            </ArgonButton>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SubCategoryModal;
